import { INotification, INotificationState } from "@/common/types/notification";

// ─── Action types ──────────────────────────────────────────────────────────
export const NOTIFICATION_ADD = "NOTIFICATION_ADD";
export const NOTIFICATION_SET_LIST = "NOTIFICATION_SET_LIST";
export const NOTIFICATION_APPEND = "NOTIFICATION_APPEND";
export const NOTIFICATION_SET_UNREAD_COUNT = "NOTIFICATION_SET_UNREAD_COUNT";
export const NOTIFICATION_MARK_READ = "NOTIFICATION_MARK_READ";
export const NOTIFICATION_MARK_ALL_READ = "NOTIFICATION_MARK_ALL_READ";
export const NOTIFICATION_REMOVE = "NOTIFICATION_REMOVE";
export const NOTIFICATION_TOGGLE_DRAWER = "NOTIFICATION_TOGGLE_DRAWER";
export const NOTIFICATION_OPEN_DRAWER = "NOTIFICATION_OPEN_DRAWER";
export const NOTIFICATION_CLOSE_DRAWER = "NOTIFICATION_CLOSE_DRAWER";
export const NOTIFICATION_SET_CONNECTED = "NOTIFICATION_SET_CONNECTED";

// ─── Action creators ───────────────────────────────────────────────────────
export const addNotification = (notification: INotification) => ({
  type: NOTIFICATION_ADD,
  payload: notification,
});

export const setNotifications = (payload: { notifications: INotification[]; total: number; unreadCount: number }) => ({
  type: NOTIFICATION_SET_LIST,
  payload,
});

export const appendNotifications = (payload: { notifications: INotification[]; unreadCount: number }) => ({
  type: NOTIFICATION_APPEND,
  payload,
});

export const setUnreadCount = (count: number) => ({
  type: NOTIFICATION_SET_UNREAD_COUNT,
  payload: count,
});

export const markRead = (id: string) => ({ type: NOTIFICATION_MARK_READ, payload: id });

export const markAllRead = () => ({ type: NOTIFICATION_MARK_ALL_READ });

export const removeNotification = (id: string) => ({
  type: NOTIFICATION_REMOVE,
  payload: id,
});

export const toggleDrawer = () => ({ type: NOTIFICATION_TOGGLE_DRAWER });
export const openDrawer = () => ({ type: NOTIFICATION_OPEN_DRAWER });
export const closeDrawer = () => ({ type: NOTIFICATION_CLOSE_DRAWER });
export const setSocketConnected = (connected: boolean) => ({
  type: NOTIFICATION_SET_CONNECTED,
  payload: connected,
});

// ─── Initial state ─────────────────────────────────────────────────────────
const initialState: INotificationState = {
  notifications: [],
  unreadCount: 0,
  isDrawerOpen: false,
  isConnected: false,
};

// ─── Reducer ───────────────────────────────────────────────────────────────
export const NotificationReducer = (state = initialState, action: any): INotificationState => {
  switch (action.type) {
    case NOTIFICATION_ADD: {
      return {
        ...state,
        notifications: [action.payload, ...state.notifications],
        unreadCount: state.unreadCount + 1,
      };
    }

    case NOTIFICATION_SET_LIST: {
      return {
        ...state,
        notifications: action.payload.notifications,
        unreadCount: action.payload.unreadCount,
      };
    }

    case NOTIFICATION_APPEND: {
      return {
        ...state,
        notifications: [...state.notifications, ...action.payload.notifications],
        unreadCount: action.payload.unreadCount,
      };
    }

    case NOTIFICATION_SET_UNREAD_COUNT: {
      return { ...state, unreadCount: action.payload };
    }

    case NOTIFICATION_MARK_READ: {
      const updated = state.notifications.map((n) =>
        n._id === action.payload && !n.isRead ? { ...n, isRead: true, readAt: new Date().toISOString() } : n
      );
      const wasUnread = state.notifications.find((n) => n._id === action.payload && !n.isRead);
      return {
        ...state,
        notifications: updated,
        unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
      };
    }

    case NOTIFICATION_MARK_ALL_READ: {
      return {
        ...state,
        notifications: state.notifications.map((n) => ({
          ...n,
          isRead: true,
          readAt: n.readAt ?? new Date().toISOString(),
        })),
        unreadCount: 0,
      };
    }

    case NOTIFICATION_REMOVE: {
      const target = state.notifications.find((n) => n._id === action.payload);
      return {
        ...state,
        notifications: state.notifications.filter((n) => n._id !== action.payload),
        unreadCount: target && !target.isRead ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
      };
    }

    case NOTIFICATION_TOGGLE_DRAWER:
      return { ...state, isDrawerOpen: !state.isDrawerOpen };

    case NOTIFICATION_OPEN_DRAWER:
      return { ...state, isDrawerOpen: true };

    case NOTIFICATION_CLOSE_DRAWER:
      return { ...state, isDrawerOpen: false };

    case NOTIFICATION_SET_CONNECTED:
      return { ...state, isConnected: action.payload };

    default:
      return state;
  }
};
